import { CustomerRecognitionFormSchema } from '../../../../../services/Recognition/Customer/schema';
import {
  useCreateCustomerRecognitionAsync,
  useGetCustomerRecognitionByIdAsync,
  useUpdateCustomerRecognitionAsync,
} from '../../../../../services/Recognition/Customer/hooks/useMutation';
import { useGetAllRecognitionGroupByLicenseAsync } from 'services/Recognition/Group/hooks/useMutations';
import { useGetAllFacialControllerByLicenseAsync } from 'services/Devices/Recognition/Controllers/hooks/useMutation';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AddressFormSchema } from 'services/AddressService/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { IRecognitionGroupSimpleProps } from '../../../../../services/Recognition/Group/types';
import { IFacialControllerSimpleProps } from '../../../../../services/Devices/Recognition/Controllers/types';
import { FaceEditionContext } from '../../../context/FaceEditionContextProvider';
import { AppContext } from '../../../../../context/AppContextProvider';
import { useGetViaCepAdress } from '../../../../../services/ViaCepService/hooks/useMutation';
import dayjs from 'dayjs';
import { Grid2, Typography } from '@mui/material';
import TextFieldComponent from '../../../../../components/TextFieldComponent';
import DatePickerComponent from '../../../../../components/DatePickerComponent';
import DropdownSelectComponent from '../../../../../components/DropdownSelectComponent';
import CheckboxComponent from '../../../../../components/CheckboxComponent';
import DropdownComponent from '../../../../../components/DropdownComponent';
import { COUNTRYOPTIONS } from '../../../../../services/AddressService/types';
import ImageFace from '../cells/ImageFace';
import ButtonComponent from '../../../../../components/ButtonComponent';
import {
  CustomerGenderTypes,
  ICustomerRecognitionCreateProps,
  ICustomerRecognitionProps,
  ICustomerRecognitionUpdateProps,
} from '../../../../../services/Recognition/Customer/types';
import { transformFirebaseLinkToBlob } from '../../../../../utils/TransformUtils';
import { IRecognitionSimpleProps } from '../../../../../services/Recognition/types';
import { t } from 'i18next';

interface ICustomerRecognitionEditFormProps {
  selectedRecognitionId?: number;
  handleSaveForm: () => void;
  selectedRecognitionData?: IRecognitionSimpleProps;
}

type CustomerRecognitionFormInput = z.infer<
  typeof CustomerRecognitionFormSchema
>;
type AddressFormInput = z.infer<typeof AddressFormSchema>;

const CustomerRecognitionEditForm = ({
  handleSaveForm,
  selectedRecognitionId,
  selectedRecognitionData,
}: ICustomerRecognitionEditFormProps) => {
  const {
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CustomerRecognitionFormInput>({
    resolver: zodResolver(CustomerRecognitionFormSchema),
    defaultValues: {
      name: '',
      cpf: '',
      cnpj: '',
      image: '',
      recognitionGroupIds: [],
      facialControllerIds: [],
      isAddress: false,
      phone: '',
      email: '',
      occupation: '',
      age: '',
    },
  });
  const [groups, setGroups] = useState<IRecognitionGroupSimpleProps[]>();
  const [controllers, setControllers] =
    useState<IFacialControllerSimpleProps[]>();
  const isAddress = watch('isAddress');
  const [address, setAddress] = useState<AddressFormInput>();
  const { selectedImage, setSelectedImage, isImageChange } =
    useContext(FaceEditionContext);
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);
  const { mutateAsync: mutateGetViaCepAddress } = useGetViaCepAdress();
  const {
    mutateAsync: mutateGetRecognition,
    isLoading: isLoadingGetRecognition,
  } = useGetCustomerRecognitionByIdAsync();
  const {
    mutateAsync: mutateCreateRecognition,
    isLoading: isLoadingCreateRecognition,
  } = useCreateCustomerRecognitionAsync();
  const {
    mutateAsync: mutateUpdateRecognition,
    isLoading: isLoadingUpdateRecognition,
  } = useUpdateCustomerRecognitionAsync();
  const {
    mutateAsync: mutateGetRecognitionGroups,
    isLoading: isLoadingGetRecognitionGroups,
  } = useGetAllRecognitionGroupByLicenseAsync();
  const {
    mutateAsync: mutateGetControllers,
    isLoading: isLoadingGetControllers,
  } = useGetAllFacialControllerByLicenseAsync();

  const genderOptions = [
    {
      label: t('recognitionPage.modalEditFace.genders.male'),
      value: CustomerGenderTypes.MALE,
    },
    {
      label: t('recognitionPage.modalEditFace.genders.female'),
      value: CustomerGenderTypes.FEMALE,
    },
    {
      label: t('recognitionPage.modalEditFace.genders.other'),
      value: CustomerGenderTypes.OTHER,
    },
  ];

  const getAddress = async (value?: string) => {
    if (!value) return;

    await mutateGetViaCepAddress(value).then((address) => {
      setValue('address.postalCode', address.postalCode);
      setValue('address.city', address.city);
      setValue('address.state', address.state);
      setValue('address.street', address.street);
      setValue('address.neighborhood', address.neighborhood);
    });
  };

  const normalizeFormData = async (data: CustomerRecognitionFormInput) => {
    if (data.validUntil) {
      data.validUntil = data.validUntil.trim().split('T')[0];
    }
    if (data.cnpj) {
      data.cnpj = data.cnpj.trim().replace(/\D/g, '');
    }

    data.cpf = data.cpf.trim().replace(/\D/g, '');
    data.phone = data.phone.trim().replace(/\D/g, '');
    data.birthDate = data.birthDate.trim().split('T')[0];
    if (isImageChange) data.image = '';
  };

  const handleSave = async (data: CustomerRecognitionFormInput) => {
    if (!selectedImage) {
      showSnackbar(
        t('recognitionPage.registerFace.youForgotToTakeAPicture'),
        'error',
      );
      return;
    } else {
      await normalizeFormData(data);
    }

    if (data.id) {
      const customer: ICustomerRecognitionUpdateProps = { ...data };
      customer.file = selectedImage as Blob;
      await mutateUpdateRecognition({
        id: data.id,
        data: customer,
      }).then(() => {
        resetAllAndShowMessage(t('recognitionPage.registerFace.updatedFace'));
      });
    } else {
      const customer: ICustomerRecognitionCreateProps = { ...data };
      customer.file = selectedImage as Blob;
      await mutateCreateRecognition(customer).then(() => {
        resetAllAndShowMessage(t('recognitionPage.registerFace.registerFace'));
      });
    }
  };

  const resetAllAndShowMessage = (message: string) => {
    setSelectedImage('');
    reset();
    if (handleSaveForm) handleSaveForm();
    showSnackbar(message, 'success');
  };

  const mapRecognitionToForm = (
    response: ICustomerRecognitionProps,
  ): CustomerRecognitionFormInput => {
    const validUntilStr = response.validUntil
      ? dayjs(response.validUntil).toISOString()
      : null;

    const birthDateStr = dayjs(response.birthDate).toISOString();

    const hasAddress = !!response.address;

    const addressDefaults = {
      id: undefined,
      postalCode: '',
      country: '',
      state: '',
      city: '',
      neighborhood: '',
      street: '',
      addrNumber: '',
      complement: '',
    };

    if (hasAddress) {
      setAddress(response.address);
      return {
        id: response.id,
        name: response.name,
        cpf: response.cpf ?? '',
        cnpj: response.cnpj ?? '',
        image: response.image ?? '',
        recognitionGroupIds: response.recognitionGroups?.map((i) => i.id) ?? [],
        facialControllerIds: response.facialControllers?.map((i) => i.id) ?? [],
        validUntil: validUntilStr,
        isAddress: true,
        address: { ...addressDefaults, ...response.address! },
        birthDate: birthDateStr,
        occupation: response.occupation,
        email: response.email,
        phone: response.phone,
        age: response.age,
        gender: response.gender,
      };
    } else {
      return {
        id: response.id,
        name: response.name,
        cpf: response.cpf ?? '',
        cnpj: response.cnpj ?? '',
        image: response.image ?? '',
        recognitionGroupIds: response.recognitionGroups?.map((i) => i.id) ?? [],
        facialControllerIds: response.facialControllers?.map((i) => i.id) ?? [],
        validUntil: validUntilStr,
        isAddress: false,
        address: null,
        birthDate: birthDateStr ?? '',
        occupation: response.occupation ?? '',
        email: response.email ?? '',
        phone: response.phone ?? '',
        age: response.age ?? '',
        gender: response.gender,
      };
    }
  };

  useEffect(() => {
    if (!isAddress) {
      setValue('address', null);
    } else {
      if (address) {
        setValue('address', address);
      } else {
        setValue('address', {
          country: '',
          state: '',
          city: '',
          neighborhood: '',
          street: '',
          postalCode: '',
          name: '',
          addrNumber: '',
          complement: '',
        });
      }
    }
  }, [isAddress, address]);

  useEffect(() => {
    if (!controllers) {
      mutateGetControllers({
        pagination: {
          pageSize: 30,
          pageNumber: 1,
        },
      }).then(setControllers);
    }
  }, [controllers]);

  useEffect(() => {
    if (!groups) {
      mutateGetRecognitionGroups({}).then(setGroups);
    }
  }, [groups]);

  useEffect(() => {
    if (!selectedRecognitionData) return;

    if (selectedRecognitionData?.image) {
      setSelectedImage(selectedRecognitionData.image);
      setValue('name', selectedRecognitionData.name);
    }
    if (selectedRecognitionData.cpf)
      setValue('cpf', selectedRecognitionData.cpf);

    if (selectedRecognitionData.facialControllersIds) {
      setValue(
        'facialControllerIds',
        selectedRecognitionData.facialControllersIds,
      );
    }
  }, [selectedRecognitionData]);

  useEffect(() => {
    if (selectedRecognitionId) {
      mutateGetRecognition(selectedRecognitionId).then(async (response) => {
        const formValues = mapRecognitionToForm(response);
        reset(formValues, { keepDefaultValues: true });
        const image = await transformFirebaseLinkToBlob(response.image);
        setSelectedImage(image);
      });
    }
  }, [selectedRecognitionId]);

  useEffect(() => {
    return () => {
      setSelectedImage('');
    };
  }, []);

  return (
    <>
      <Grid2
        container
        direction={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        size={12}
        spacing={3}
      >
        <Grid2
          display={'flex'}
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent={'center'}
          alignItems={{ xs: 'center', md: 'normal' }}
          size={12}
          gap={{ xs: 0, md: 3 }}
        >
          <Grid2 size={{ xs: 12, lg: 6 }}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('recognitionPage.modalEditFace.name')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'name'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('recognitionPage.modalEditFace.cpf')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  mask={'000.000.000-00'}
                />
              )}
              name={'cpf'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('recognitionPage.modalEditFace.cnpj')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  isOptional={true}
                  mask={'00.000.000/0000-00'}
                />
              )}
              name={'cnpj'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DatePickerComponent
                  value={value ? dayjs(value).utc() : null}
                  onChange={(value) => {
                    const date = dayjs(value);
                    onChange(
                      date.isValid() ? dayjs(value).toISOString() : null,
                    );
                  }}
                  errorMessage={error?.message}
                  label={t('recognitionPage.modalEditFace.faceValidity')}
                  isOptional={true}
                  disablePast={true}
                />
              )}
              name={'validUntil'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DropdownComponent
                  label={t('recognitionPage.modalEditFace.genders.gender')}
                  value={value}
                  optionLabel={'label'}
                  optionValue={'value'}
                  options={genderOptions}
                  onChange={onChange}
                />
              )}
              name={'gender'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('recognitionPage.modalEditFace.age')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'age'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DatePickerComponent
                  value={value ? dayjs(value).utc() : null}
                  onChange={(value) => {
                    const date = dayjs(value);
                    onChange(
                      date.isValid() ? dayjs(value).toISOString() : null,
                    );
                  }}
                  errorMessage={error?.message}
                  label={t('recognitionPage.modalEditFace.birthdayDate')}
                />
              )}
              name={'birthDate'}
              control={control}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DropdownSelectComponent
                  label={t('recognitionPage.modalEditFace.faceGroup')}
                  options={groups}
                  labelField={'name'}
                  valueField={'id'}
                  errorMessage={error?.message}
                  selectedValues={value}
                  onSelectedValues={onChange}
                />
              )}
              name={'recognitionGroupIds'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DropdownSelectComponent
                  label={t('recognitionPage.modalEditFace.facialController')}
                  options={controllers}
                  labelField={'name'}
                  valueField={'id'}
                  errorMessage={error?.message}
                  selectedValues={value}
                  onSelectedValues={onChange}
                />
              )}
              name={'facialControllerIds'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('recognitionPage.modalEditFace.cellPhone')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  mask={'(00) 0 0000-0000'}
                  maxLength={16}
                />
              )}
              name={'phone'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('recognitionPage.modalEditFace.email')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  isOptional={true}
                />
              )}
              name={'email'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('recognitionPage.modalEditFace.occupation')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  isOptional={true}
                />
              )}
              name={'occupation'}
              control={control}
            />
            <Grid2
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              mt={4}
            >
              <Controller
                name="isAddress"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CheckboxComponent
                    label="Adicionar Endereço (Opcional)"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Grid2>
          </Grid2>
        </Grid2>
        {isAddress && (
          <>
            <Grid2 display={'flex'} justifyContent={'start'} width={'100%'}>
              <Typography variant={'h5'} component={'h2'} fontWeight={'bolder'}>
                {t('recognitionPage.modalEditFace.address.address')}
              </Typography>
            </Grid2>
            <Grid2
              display={'flex'}
              flexDirection={{ xs: 'column', md: 'row' }}
              justifyContent={'center'}
              alignItems={{ xs: 'center', md: 'normal' }}
              size={12}
              gap={{ xs: 0, md: 3 }}
            >
              <Grid2 size={{ xs: 12, lg: 6 }}>
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t(
                        'recognitionPage.modalEditFace.address.postalCode',
                      )}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      onBlur={() => getAddress(value)}
                      errorMessage={error?.message}
                      value={value}
                      mask={'00000-000'}
                    />
                  )}
                  name={'address.postalCode'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <DropdownComponent
                      label={t('recognitionPage.modalEditFace.address.country')}
                      options={COUNTRYOPTIONS}
                      optionLabel={'label'}
                      optionValue={'value'}
                      errorMessage={error?.message}
                      value={value}
                      onChange={onChange}
                    />
                  )}
                  name={'address.country'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t('recognitionPage.modalEditFace.address.state')}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.state'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t('recognitionPage.modalEditFace.address.city')}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.city'}
                  control={control}
                />
              </Grid2>
              <Grid2
                size={{ xs: 12, lg: 6 }}
                display={'column'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t(
                        'recognitionPage.modalEditFace.address.neighborhood',
                      )}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.neighborhood'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t('recognitionPage.modalEditFace.address.street')}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.street'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t(
                        'recognitionPage.modalEditFace.address.addrNumber',
                      )}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      isOptional={true}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.addrNumber'}
                  control={control}
                />
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      label={t(
                        'recognitionPage.modalEditFace.address.complement',
                      )}
                      isOptional={true}
                      onChangeText={(val) => onChange(val)}
                      type={'text'}
                      errorMessage={error?.message}
                      value={value}
                    />
                  )}
                  name={'address.complement'}
                  control={control}
                />
              </Grid2>
            </Grid2>
          </>
        )}
        <Grid2
          size={8}
          display={'column'}
          alignItems={'center'}
          justifyContent={'center'}
          mb={3}
        >
          <ImageFace handleCancelImageEdition={() => setSelectedImage('')} />
        </Grid2>
      </Grid2>
      <ButtonComponent
        variant="contained"
        color="primary"
        loading={isLoadingUpdateRecognition || isLoadingCreateRecognition}
        fullWidth
        onClick={handleSubmit(handleSave)}
      >
        {t('recognitionPage.modalEditFace.buttonCaptureImage.save')}
      </ButtonComponent>
    </>
  );
};

export default CustomerRecognitionEditForm;
